/**
 * The dates she watches for, and the season she reads the rest of the year
 * through. Pure and local: every function here takes a `Date` and returns an
 * answer, so the client and the server agree without either asking the other.
 */

export type Season = 'winter' | 'spring' | 'summer' | 'autumn'

export interface CalendarHoliday {
  key: string
  label: string
}

interface FixedHoliday {
  key: string
  label: string
  month: number
  day: number
}

const FIXED_HOLIDAYS: readonly FixedHoliday[] = [
  { key: 'new_year', label: 'Naujieji metai', month: 1, day: 1 },
  { key: 'independence', label: 'Vasario 16-oji', month: 2, day: 16 },
  { key: 'restoration', label: 'Kovo 11-oji', month: 3, day: 11 },
  { key: 'jonines', label: 'Joninės', month: 6, day: 24 },
  // Also Katė's canon birthday (2025-08-15) — the two happening to share a
  // date is a coincidence the catalogue is allowed to notice.
  { key: 'zoline', label: 'Žolinė', month: 8, day: 15 },
  { key: 'visu_sventuju', label: 'Visų Šventųjų diena', month: 11, day: 1 },
  // Her favourite, and the correct date for it: Vėlinės is the second, the
  // day for the dead specifically, not the first. See PERSONA.md.
  { key: 'velines', label: 'Vėlinės', month: 11, day: 2 },
  { key: 'christmas_eve', label: 'Kūčios', month: 12, day: 24 },
  { key: 'christmas', label: 'Kalėdos', month: 12, day: 25 },
  { key: 'new_years_eve', label: 'Naujųjų išvakarės', month: 12, day: 31 },
]

export const KATE_BIRTHDAY = { month: 8, day: 15 } as const

/**
 * Western Easter, by the anonymous Gregorian algorithm — accurate for any
 * Gregorian-calendar year, which is all this app will ever be asked about.
 */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return { month, day }
}

/**
 * Užgavėnės — Shrove Tuesday, 47 days before Easter Sunday. Movable, and the
 * most on-brand date in the year for a narrator with opinions about frying:
 * the one day the tradition is to eat until the pancakes run out.
 */
export function uzgavenesDate(year: number): { month: number; day: number } {
  const easter = easterSunday(year)
  const date = new Date(Date.UTC(year, easter.month - 1, easter.day))
  date.setUTCDate(date.getUTCDate() - 47)
  return { month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

/** The first Sunday-of-the-month holidays: Motinos and Tėvo diena. */
function nthSundayOfMonth(year: number, month: number, n: number): { month: number; day: number } {
  const first = new Date(Date.UTC(year, month - 1, 1))
  const firstSunday = 1 + ((7 - first.getUTCDay()) % 7)
  return { month, day: firstSunday + (n - 1) * 7 }
}

/** Every holiday landing on this date — almost always none, occasionally one. */
export function holidaysOn(date: Date): CalendarHoliday[] {
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()
  const found: CalendarHoliday[] = []

  for (const holiday of FIXED_HOLIDAYS) {
    if (holiday.month === month && holiday.day === day) {
      found.push({ key: holiday.key, label: holiday.label })
    }
  }
  const easter = easterSunday(year)
  if (easter.month === month && easter.day === day) found.push({ key: 'velykos', label: 'Velykos' })
  const uzgavenes = uzgavenesDate(year)
  if (uzgavenes.month === month && uzgavenes.day === day) {
    found.push({ key: 'uzgavenes', label: 'Užgavėnės' })
  }
  const mothers = nthSundayOfMonth(year, 5, 1)
  if (mothers.month === month && mothers.day === day) {
    found.push({ key: 'motinos_diena', label: 'Motinos diena' })
  }
  const fathers = nthSundayOfMonth(year, 6, 1)
  if (fathers.month === month && fathers.day === day) {
    found.push({ key: 'tevo_diena', label: 'Tėvo diena' })
  }
  return found
}

export function isHolidayToday(date: Date): boolean {
  return holidaysOn(date).length > 0
}

export function seasonOf(date: Date): Season {
  const month = date.getMonth() + 1
  if (month === 12 || month <= 2) return 'winter'
  if (month <= 5) return 'spring'
  if (month <= 8) return 'summer'
  return 'autumn'
}

/** November on its own: the month she leans gloomy in, per PERSONA.md. */
export function isNovember(date: Date): boolean {
  return date.getMonth() === 10
}

/** `birthDate` is the stored `YYYY-MM-DD`; only month and day are compared. */
export function isBirthday(date: Date, birthDate: string | null | undefined): boolean {
  if (!birthDate) return false
  const parts = birthDate.split('-')
  const month = Number(parts[1])
  const day = Number(parts[2])
  if (!Number.isFinite(month) || !Number.isFinite(day)) return false
  return date.getMonth() + 1 === month && date.getDate() === day
}

export function isKateBirthday(date: Date): boolean {
  return date.getMonth() + 1 === KATE_BIRTHDAY.month && date.getDate() === KATE_BIRTHDAY.day
}

/**
 * `createdAt` is the account's own creation timestamp. Only fires from the
 * second year on — a birthday needs at least one candle to be one.
 */
export function isAppAnniversary(date: Date, createdAt: string | null | undefined): boolean {
  if (!createdAt) return false
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) return false
  return (
    date.getMonth() === created.getMonth() &&
    date.getDate() === created.getDate() &&
    date.getFullYear() > created.getFullYear()
  )
}
